import React, { useState, useEffect, Suspense, lazy } from 'react';
import { base44 } from '@/api/base44Client';
import { withRateLimitRetry } from '@/lib/rateLimitRetry';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { User } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import SideDrawer from '../components/SideDrawer';
import BroadcastCarousel from '../components/BroadcastCarousel';
import HeroSection from '../components/HeroSection';
import { rebuildHomeCache } from '@/lib/homeCache';

// Lazy load non-critical components
const InstagramCarousel = lazy(() => import('../components/InstagramCarousel'));
import AppStoreBadges, { isCustomDomain } from '@/components/AppStoreBadges';
import DesktopHome from '@/components/home/DesktopHome';
import { getUserFirstName } from '@/lib/userName';
const CarouselLoader = () => <div className="h-64 bg-gray-100 animate-pulse" />;

// Stale-while-revalidate cache for the hero — it's the page's LCP element, so
// paint the last known hero immediately instead of waiting for the API.
const HERO_CACHE_KEY = 'home_hero_cache';

const readCachedHero = () => {
  try {
    const raw = localStorage.getItem(HERO_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedHero = (hero) => {
  try {
    if (hero) localStorage.setItem(HERO_CACHE_KEY, JSON.stringify(hero));
    else localStorage.removeItem(HERO_CACHE_KEY);
  } catch {
    // Storage unavailable (private mode) — cache is best-effort only.
  }
};

export default function Home() {
  const [user, setUser] = useState(null);
  const [homeLinks, setHomeLinks] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [businessName, setBusinessName] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [appLinks, setAppLinks] = useState({});
  const [hero, setHero] = useState(readCachedHero);
  const navigate = useNavigate();

  useEffect(() => {
    // Handle ?redirect= param — used by email deep links on iOS WebView
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect && /^\/(?![/\\])/.test(redirect)) {
      navigate(redirect, { replace: true });
      return;
    }
    loadUser();
    loadFromCache();
  }, []);

  const loadFromCache = async (forceRebuild = false) => {
    try {
      if (forceRebuild) {
        await rebuildHomeCache();
      }
      const cacheData = await withRateLimitRetry(() => base44.entities.HomeCache.list());
      if (cacheData.length > 0) {
        const cache = cacheData[0];
        setBroadcasts(cache.broadcasts || []);
        setHero(cache.hero || null);
        writeCachedHero(cache.hero || null);
        setHomeLinks(cache.home_links || []);
        if (cache.contact_info?.business_name) {
          setBusinessName(cache.contact_info.business_name);
        }
        setAppLinks({
          appStoreUrl: cache.contact_info?.app_store_url,
          playStoreUrl: cache.contact_info?.google_play_url
        });
      } else {
        // No cache yet — build it on first load
        await rebuildHomeCache();
        await loadFromCache(false);
      }
    } catch (error) {
      console.error('Error loading home cache:', error);
    }
  };

  const loadUser = () => {
    try {
      const customerData = localStorage.getItem('customer_data');
      if (customerData) {
        const data = JSON.parse(customerData);
        setUser({
          full_name: data.full_name || data.first_name || 'Guest',
          email: data.email || '',
          phone_number: data.phone_number,
          square_customer_id: data.square_customer_id
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    }
  };

  const handleRefresh = async () => {
    await loadFromCache(true);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good Evening';
  };

  const firstName = getUserFirstName(user) || 'there';

  const handleLinkClick = (link) => {
    if (link.link_type === 'internal_page') {
      navigate(createPageUrl(link.link_value));
    } else {
      window.location.href = link.link_value;
    }
  };

  const handleBroadcastClick = (broadcast) => {
    if (broadcast.link_type && broadcast.link_value) {
      if (broadcast.link_type === 'internal_page') {
        // Check if link_value contains location and/or category (format: "Order||locationId" or "Order||locationId||categoryId")
        if (broadcast.link_value.includes('||')) {
          const parts = broadcast.link_value.split('||');
          const page = parts[0];
          const locationId = parts[1];
          const categoryId = parts[2];

          let url = createPageUrl(page);
          if (locationId) url += `?location=${locationId}`;
          if (categoryId) url += locationId ? `&category=${categoryId}` : `?category=${categoryId}`;

          navigate(url);
        } else {
          navigate(createPageUrl(broadcast.link_value));
        }
      } else {
        window.location.href = broadcast.link_value;
      }
    }
  };

  const getLinkIcon = (buttonName) => {
    const icons = {
      BOOK_A_TABLE: '🍽️',
      CONTACT_US: '📞',
      MENUS_ALLERGENS: '📄',
      ONLINE_ORDERING: '🛒'
    };
    return icons[buttonName] || '✨';
  };

  // Gold color
  const gold = '#C9A84C';
  const customDomain = isCustomDomain();

  return (
    <div className={`min-h-screen lg:pb-0 bg-white lg:pt-0 ${customDomain ? 'pb-0' : 'pt-24 pb-40'}`}>

      {/* Header - Notification bell and Account button */}
      <div className={`${customDomain ? 'hidden' : 'lg:hidden'} fixed top-0 left-0 right-0 bg-white z-40 min-h-[88px] flex items-center pt-4 pr-4 pl-4 pb-2`} style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <div className="flex items-center justify-between w-full mt-8">
          <NotificationBell />
          <button
            onClick={() => navigate(createPageUrl('Account'))}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2"
            aria-label="Account">
            
            <User className="w-6 h-6" style={{ color: '#1A2744' }} />
          </button>
        </div>
      </div>

      <SideDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <DesktopHome hero={hero?.is_enabled ? hero : null} businessName={businessName} broadcasts={broadcasts} onBroadcastClick={handleBroadcastClick} allScreens={customDomain} />

      <div className={customDomain ? 'hidden' : 'lg:hidden'}>
      <>
         {/* Hero Section - Mobile and Desktop (top of page) */}
         {hero &&
        <div className="mx-auto px-4 md:px-4 lg:px-8 lg:max-w-7xl w-full">
             <div className="rounded-2xl overflow-hidden">
               <HeroSection hero={hero} />
             </div>
           </div>
        }

         {/* App download links — mobile & tablet only (footer is desktop-only) */}
         <div className="lg:hidden px-4 mt-6">
           <AppStoreBadges
             appStoreUrl={appLinks.appStoreUrl}
             playStoreUrl={appLinks.playStoreUrl}
           />
         </div>

         {/* Broadcast Notifications Carousel */}
                   <div className="w-full md:max-w-none lg:max-w-7xl mt-4 px-4 md:px-4 pt-3 lg:px-8 lg:mx-auto">
           <BroadcastCarousel broadcasts={broadcasts} onBroadcastClick={handleBroadcastClick} />
         </div>

        {/* Main content */}
        


























        

        {/* Instagram Section */}
        <Suspense fallback={<CarouselLoader />}>
          <InstagramCarousel />
        </Suspense>
      </>
      </div>
    </div>);

}